import { authDisabled, getAccessToken } from '@auth/keycloak'
import { getConfig } from '../config'

const API_BASE_URL = getConfig().API_BASE_URL

/** Error thrown by {@link customFetch} for a non-2xx response, carrying the parsed body. */
export class ApiError extends Error {
  readonly status: number
  /** Parsed JSON body when the response was JSON, otherwise the raw text. */
  readonly body: unknown

  constructor(status: number, message: string, body: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.body = body
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError
}

async function parseResponseBody<TResponse>(
  response: Response,
): Promise<TResponse> {
  if (response.status === 204) {
    return undefined as TResponse
  }

  const contentType = response.headers.get('content-type')
  if (contentType?.includes('application/json')) {
    return (await response.json()) as TResponse
  }
  if (contentType && !contentType.startsWith('text/')) {
    return (await response.blob()) as TResponse
  }
  return (await response.text()) as TResponse
}

/** Orval mutator: adds the Keycloak bearer token and normalizes errors and response bodies. */
export async function customFetch<TResponse>(
  url: string,
  options: RequestInit,
): Promise<TResponse> {
  const token = await getAccessToken()
  const headers = new Headers(options.headers)
  if (token) {
    headers.set('Authorization', `Bearer ${token}`)
  }
  // Local dev only: act as another user against a backend started with AUTH_DISABLED=true.
  const devUser = import.meta.env.VITE_DEV_USER
  if (authDisabled && devUser) {
    headers.set('X-Dev-User', devUser)
  }

  const response = await fetch(`${API_BASE_URL}${url}`, {
    ...options,
    mode: 'cors',
    headers,
  })

  if (!response.ok) {
    const text = await response.text()
    let body: unknown = text
    const isJson = response.headers
      .get('content-type')
      ?.includes('application/json')
    if (isJson && text) {
      try {
        body = JSON.parse(text)
      } catch {
        /* keep raw text */
      }
    }
    const detail =
      body && typeof body === 'object' && 'message' in body
        ? String((body as { message: unknown }).message)
        : text || response.statusText
    throw new ApiError(
      response.status,
      `API request failed (${response.status}): ${detail}`,
      body,
    )
  }

  return parseResponseBody<TResponse>(response)
}
